import { BadgeCheck, Bell, Handshake, HeartHandshake, Map, Menu, Store, User, X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { guardianApi } from '../api/endpoints'
import { useAuth } from '../auth/AuthContext'
import { marketplaceHiddenFor } from '../utils/marketplaceAccess'
import { AppDownloadQr } from './AppDownloadQr'
import { AvatarSwitcher } from './AvatarSwitcher'
import { Logo } from './Logo'
import { ProfileSwitcher } from './ProfileSwitcher'
import { SetupReminderBanner } from './SetupReminderBanner'
import { SidebarBanners } from './SidebarBanners'

/**
 * The sidebar's "Access … Profile" switch / create button, hidden for now. A second role is added
 * by registering the same email, and the role is chosen at sign-in. Flip back to true to restore.
 */
const SHOW_PROFILE_SWITCHER = false

interface NavEntry {
  to: string
  label: string
  icon: ReactNode
  badge?: number
}

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
    isActive ? 'bg-ink-900 text-ink-50' : 'text-ink-600 hover:bg-ink-100'
  }`

function NavGroup({ title, entries }: { title: string; entries: NavEntry[] }) {
  if (entries.length === 0) return null
  return (
    <div className="mt-5 first:mt-0">
      <div className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400">{title}</div>
      <div className="space-y-0.5">
        {entries.map((e) => (
          <NavLink key={e.to} to={e.to} className={linkClass}>
            <span className="shrink-0 text-ink-400">{e.icon}</span>
            <span className="min-w-0 flex-1 truncate">{e.label}</span>
            {e.badge ? (
              <span className="grid h-5 min-w-[1.25rem] shrink-0 place-items-center rounded-full bg-sun-400 px-1 text-[11px] font-bold text-ink-900">
                {e.badge}
              </span>
            ) : null}
          </NavLink>
        ))}
      </div>
    </div>
  )
}

export function Layout() {
  const { profile } = useAuth()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [pendingLinkCount, setPendingLinkCount] = useState(0)

  // The badge counts link requests waiting on this participant - the only notification the
  // backend can answer for today.
  useEffect(() => {
    if (!profile) {
      setPendingLinkCount(0)
      return
    }
    guardianApi
      .listMyLinks()
      .then((links) => setPendingLinkCount(links.filter((l) => l.awaitingMyApproval).length))
      .catch(() => setPendingLinkCount(0))
  }, [profile, location.pathname])

  const isSupply = profile?.persona === 'SUPPLY'
  const isGuardian = profile?.entityKind === 'GUARDIAN'

  // The drawer covers the page on phones, so it must not survive a navigation.
  useEffect(() => setMenuOpen(false), [location.pathname])

  // A guardian can look for a tutor on their child's behalf - their own Category/Sub Category on
  // their own profile is what the map matches against, the same as a learner's - so they get the
  // same Browse entries as everyone else rather than none at all.
  //
  // The marketplace is the demand side's shopfront - a list of providers to choose from. A
  // provider browsing it is browsing their own competitors, which is not what they came for, so
  // they get the map (where learners are) and nothing else.
  //
  // Some of the demand side does not get it either - see marketplaceHiddenFor. With a student
  // off it the map is their only way to discover anyone, which is why the map entry is not
  // conditional on anything.
  const showMarketplace = !isSupply && !marketplaceHiddenFor(profile?.entityKind)
  const browse: NavEntry[] = [
    { to: '/map', label: 'YellowDots Map', icon: <Map size={16} /> },
    ...(showMarketplace ? [{ to: '/marketplace', label: 'Marketplace', icon: <Store size={16} /> }] : []),
  ]

  const notifications: NavEntry = {
    to: '/notifications',
    label: 'Notifications',
    icon: <Bell size={16} />,
    badge: pendingLinkCount,
  }

  // Profile for everyone now, guardian included - it is where a guardian states what they are
  // looking for on their child's behalf (the same Category/Sub Category a learner would fill in),
  // and where anyone reviews what they registered with. "My children" sits beside it rather than
  // replacing it: linking/approving children is still its own page, not folded into this one.
  //
  // "My services" is the only entry that differs otherwise: a learner's equivalent, "My needs",
  // is still a stub, and needs are captured on the profile in the meantime.
  const mine: NavEntry[] = [
    { to: '/profile', label: 'Profile', icon: <User size={16} /> },
    // Take 4C Assessment and AI Certification, as cards - learners only: a guardian is not the one
    // assessed or certified, so the page has nothing for them.
    ...(!isSupply && !isGuardian
      ? [{ to: '/certification', label: 'Certification', icon: <BadgeCheck size={16} /> }]
      : []),
    ...(isGuardian ? [{ to: '/guardian', label: 'My children', icon: <HeartHandshake size={16} /> }] : []),
    // "My services" for everyone in the rail, deliberately: the page it opens is titled for
    // the entity - Admissions, Programmes, Internships - and one stable label here keeps the
    // navigation reading the same for every kind of provider.
    ...(isSupply ? [{ to: '/services', label: 'My services', icon: <Store size={16} /> }] : []),
  ]

  // The rail is deliberately short on both sides. Every entry it drops - my needs, engagements,
  // trust & safety, growth, the tutor listing - is a "coming soon" stub today, so listing them
  // would only advertise dead ends. The routes still exist: this hides them, it does not revoke
  // them, so each can come back beside the page that earns it.
  const actions: NavEntry[] = [
    { to: '/connections', label: 'Connections', icon: <Handshake size={16} /> },
    notifications,
  ]

  const sidebar = (
    <>
      {/* The logo goes home, as it does on every other page. "/" sends a signed-out visitor to
          the landing page and a signed-in one straight to their profile, since HomePage
          redirects - so one destination covers both. */}
      <div className="px-3 py-4">
        <Link to="/" aria-label="YellowDots home" className="inline-block">
          <Logo />
        </Link>
      </div>
      {/* min-h-0 matters: a flex child defaults to min-height:auto, which refuses to shrink below
          its content, so without it the nav pushes off the bottom of a short window instead of
          scrolling itself. The partner links live inside here too, right after Notifications
          (the last entry of "Actions"), rather than pinned to the bottom of the rail - otherwise
          they end up stranded below a gap on any window taller than the nav itself.
          Tutors/centres are on the supply side of these same partners (CareerLauncher, mySATHI,
          Talevate) - promoting them as if they were study/job resources for a provider to use is
          the wrong pitch, so this is learner/guardian-only. */}
      <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        <NavGroup title="Browse" entries={browse} />
        <NavGroup title="My profile(s)" entries={mine} />
        {/* Under that heading rather than in it: the group's entries are places to go, and this is
            a change of who you are going there as. The plural in the heading was written for it. */}
        {SHOW_PROFILE_SWITCHER && profile && !isGuardian && <ProfileSwitcher profile={profile} />}
        <NavGroup title="Actions" entries={actions} />
        {!isSupply && <SidebarBanners />}
        {/* Unlike the partner links above (learner/guardian-only - the wrong pitch for a
            provider), the app itself is for everyone: tutors, guardians, schools, colleges and
            corporates all have their own view of it, so this is not gated on persona. */}
        <div className={isSupply ? 'border-t border-ink-200/70 pt-3' : ''}>
          <AppDownloadQr />
        </div>
      </nav>
    </>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop rail. Fixed so the main column scrolls under it, as in the reference. */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-ink-200/70 bg-white lg:flex">
        {sidebar}
      </aside>

      {/* Phone/tablet drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-[1000] lg:hidden">
          <button
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 h-full w-full bg-ink-900/40"
          />
          <aside className="absolute inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col bg-white shadow-xl">
            <button
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="absolute right-2 top-3 grid h-9 w-9 place-items-center rounded-full text-ink-500 hover:bg-ink-100"
            >
              <X size={18} />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 border-b border-ink-200/70 bg-white">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5">
            <button
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-ink-200 text-ink-600 transition hover:bg-ink-100 lg:hidden"
            >
              <Menu size={17} />
            </button>
            <div className="min-w-0 lg:hidden">
              <Link to="/" aria-label="YellowDots home" className="inline-block">
                <Logo />
              </Link>
            </div>
            <div className="hidden lg:block" />
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              {/* Replaces the old plain name/role text and logout button - switching or adding a
                  Student/Tutor/Parent role, and logging out, all live in the one dropdown now. */}
              {profile && <AvatarSwitcher />}
            </div>
          </div>
        </header>

        {profile && <SetupReminderBanner profile={profile} />}

        <main className="mx-auto max-w-6xl px-4 py-6 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
